import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API, resolveImg } from '../lib/img';
import { Snowflake, Flame, Target, ArrowRight, BookOpen } from 'lucide-react';
import { authors } from '../mock';

const DEFAULT_HOME = {
  hero_title: 'The Goldilocks Zone',
  hero_subtitle: 'How Leaders Build the Conditions for Continuous Evolution',
  hero_tagline: 'Available now.',
  both_title: 'Both teams were right',
  both_paragraphs: [],
  zones_title: 'Understanding the Zones',
  zones_intro: '',
  zones: [],
  take_title: "What you'll take from it",
  take_text: '',
  take_link_text: 'Read more about the book',
  authors_title: 'Written from both sides',
  author_blurbs: [],
  authors_closing: '',
  authors_link_text: 'Meet the authors',
  launch_title: 'Get the book',
  launch_text: 'The Goldilocks Zone is available now.'
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [bookCover, setBookCover] = useState('https://customer-assets.emergentagent.com/job_balanced-change/artifacts/ljo556l7_4eea1690-43b4-468c-a2dd-3248c467d977.png');
  const [c, setC] = useState(DEFAULT_HOME);

  useEffect(() => {
    fetch(`${API}/api/settings/book-cover`)
      .then((r) => r.json())
      .then((d) => { if (d && d.book_cover_url) setBookCover(d.book_cover_url); })
      .catch(() => {});
    fetch(`${API}/api/content/home`)
      .then((r) => r.json())
      .then((d) => { if (d && d.content) setC({ ...DEFAULT_HOME, ...d.content }); })
      .catch(() => {});
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const zoneIcons = {
    Snowflake: Snowflake,
    Flame: Flame,
    Target: Target
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          minHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#FFFFFF',
          padding: '48px 40px 80px',
          position: 'relative',
          overflow: 'hidden'
        }}>

        {/* Subtle decorative element */}
        <div
          style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(201, 162, 75, 0.10) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />


        <div
          style={{
            maxWidth: '1200px',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}
          className="hero-grid">

          <style>{`
            @media (max-width: 768px) {
              .hero-grid {
                grid-template-columns: 1fr !important;
                gap: 40px !important;
              }
            }
          `}</style>

          {/* Text Content */}
          <div style={{ animation: 'fadeIn 0.8s ease-in' }}>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 5vw, 4rem)',
                fontWeight: '500',
                lineHeight: '1.2',
                marginBottom: '24px',
                color: '#111111',
                wordWrap: 'break-word',
                hyphens: 'auto'
              }}>

              {c.hero_title}
            </h1>
            <p
              style={{
                fontSize: 'clamp(1.2rem, 2vw, 1.5rem)',
                lineHeight: '1.6',
                color: '#333333',
                marginBottom: '12px',
                fontWeight: '400'
              }}>

              {c.hero_subtitle}
            </p>
            <p
              style={{
                fontSize: '1rem',
                color: '#999999',
                marginBottom: '40px',
                fontStyle: 'italic'
              }}>

              {c.hero_tagline}
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <button
                className="btn-primary"
                onClick={() => {
                  document.getElementById('email-signup')?.scrollIntoView({ behavior: 'smooth' });
                }}>

                Register for Updates
              </button>
              <Link to="/buy">
                <button className="btn-secondary" data-testid="hero-order-button">Order</button>
              </Link>
            </div>
          </div>

          {/* Book Cover */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '24px',
              animation: 'fadeIn 1s ease-in'
            }}
          >
            <img
              src={resolveImg(bookCover)}
              alt="The Goldilocks Zone Book Cover"
              style={{
                width: '100%',
                maxWidth: '420px',
                height: 'auto',
                display: 'block'
              }}
            />
          </div>
        </div>
      </section>

      {/* Both teams were right */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title">{c.both_title}</h2>
          <div style={{ marginTop: '32px' }}>
            {(c.both_paragraphs || []).map((para, i) => (
              <p
                key={i}
                style={{
                  fontSize: '1.15rem',
                  lineHeight: '1.8',
                  color: '#333333',
                  marginBottom: '20px'
                }}>
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Zones Section */}
      <section
        style={{
          backgroundColor: '#FFFFFF',
          padding: '100px 40px'
        }}>

        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2
            className="section-title"
            style={{ textAlign: 'center', marginBottom: '32px' }}>

            {c.zones_title}
          </h2>
          <p
            style={{
              maxWidth: '900px',
              margin: '0 auto 60px',
              textAlign: 'center',
              fontSize: '1.15rem',
              lineHeight: '1.8',
              color: '#333333'
            }}>
            {c.zones_intro}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '40px'
            }}>

            {(c.zones || []).map((zone, index) => {
              const IconComponent = zoneIcons[zone.icon] || Target;
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#FFFFFF',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                  }}>

                  <div
                    style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      backgroundColor:
                      zone.title === 'Goldilocks Zone' ? '#111111' : '#F4F1E8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '24px'
                    }}>

                    <IconComponent
                      size={32}
                      color={zone.title === 'Goldilocks Zone' ? '#C9A24B' : '#111111'}
                      strokeWidth={1.5} />

                  </div>
                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.5rem',
                      fontWeight: '600',
                      marginBottom: '16px',
                      color: zone.title === 'Goldilocks Zone' ? '#9A7B2E' : '#111111'
                    }}>

                    {zone.title}
                  </h3>
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: '#333333'
                    }}>

                    {zone.description}
                  </p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* What you'll take from it */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title">{c.take_title}</h2>
          <p
            style={{
              fontSize: '1.15rem',
              lineHeight: '1.8',
              color: '#333333',
              marginTop: '32px',
              marginBottom: '24px'
            }}>
            {c.take_text}
          </p>
          <Link
            to="/about-the-book"
            style={{
              color: '#9A7B2E',
              fontSize: '1rem',
              fontWeight: '600',
              textDecoration: 'none',
              borderBottom: '2px solid #C9A24B',
              paddingBottom: '2px',
              transition: 'opacity 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}>
            {c.take_link_text}
          </Link>
        </div>
      </section>

      {/* Meet the Authors */}
      <section
        style={{
          backgroundColor: '#FFFFFF',
          padding: '100px 40px'
        }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '60px' }}>
            {c.authors_title}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '60px',
              marginBottom: '40px'
            }}>

            {authors.map((author, idx) => {
              const authorImages = {
                1: 'https://customer-assets.emergentagent.com/job_7bc58836-8679-4660-ae72-45bd6fb4858b/artifacts/vwh5vx20_craig-colour.jpg',
                2: 'https://customer-assets.emergentagent.com/job_7bc58836-8679-4660-ae72-45bd6fb4858b/artifacts/8n04n5zf_nadir-colour.jpg'
              };

              return (
                <div
                  key={author.id}
                  style={{
                    textAlign: 'center'
                  }}>

                  <img
                    src={authorImages[author.id]}
                    alt={author.name}
                    style={{
                      width: '200px',
                      height: '200px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                      margin: '0 auto 24px',
                      display: 'block',
                      border: '4px solid #FFFFFF',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      filter: 'grayscale(100%)',
                      transition: 'filter 0.4s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => navigate(`/about-the-authors#author-${author.id}`)}
                    onMouseEnter={(e) => { e.currentTarget.style.filter = 'grayscale(0%)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.filter = 'grayscale(100%)'; }} />

                  <h3
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.8rem',
                      fontWeight: '600',
                      marginBottom: '12px',
                      color: '#111111'
                    }}>

                    {author.name}
                  </h3>
                  <p
                    style={{
                      fontSize: '1rem',
                      lineHeight: '1.7',
                      color: '#333333',
                      maxWidth: '340px',
                      margin: '0 auto'
                    }}>

                    {(c.author_blurbs && c.author_blurbs[idx]) || ''}
                  </p>
                </div>);

            })}
          </div>
          <p
            style={{
              textAlign: 'center',
              fontSize: '1.15rem',
              lineHeight: '1.8',
              color: '#333333',
              marginBottom: '32px'
            }}>
            {c.authors_closing}
          </p>
          <div style={{ textAlign: 'center' }}>
            <Link
              to="/about-the-authors"
              style={{
                color: '#9A7B2E',
                fontSize: '1rem',
                fontWeight: '600',
                textDecoration: 'none',
                borderBottom: '2px solid #C9A24B',
                paddingBottom: '2px',
                transition: 'opacity 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.7'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}>
              {c.authors_link_text}
            </Link>
          </div>
        </div>
      </section>

      {/* Get the book */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title">{c.launch_title}</h2>
          <p
            style={{
              fontSize: '1.15rem',
              lineHeight: '1.8',
              color: '#333333',
              marginTop: '24px',
              marginBottom: '40px'
            }}>
            {c.launch_text}
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              className="btn-primary"
              onClick={() => {
                document.getElementById('email-signup')?.scrollIntoView({ behavior: 'smooth' });
              }}>
              Register for Updates
            </button>
            <Link to="/buy">
              <button className="btn-secondary" data-testid="launch-order-button">Order</button>
            </Link>
          </div>
        </div>
      </section>

    </div>);

};

export default Home;